import { useEffect, useState } from 'react';
import './welcome-screen.css';
import { ListView } from '../components/list-view/list-view';
import { helpMeConfiguration } from '../config/help.me.config';
import { RightForMySituationConfiguration } from '../config/situation.config';
import { ConsiderRoi } from '../components/consider-roi/consider-roi';
import { useNavigate } from 'react-router';
import { DeepDive } from '../components/deep-dive/deep-dive';
import { useDispatch } from 'react-redux';
import * as SharedActions from '../../../shared/core/shared.action';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowRight,
	faArrowUpRightFromSquare,
	faBolt,
	faBook,
	faBookOpen,
	faBullseye,
	faChartLine,
	faClipboardCheck,
	faClock,
	faComments,
	faFolderOpen,
	faLayerGroup,
	faLightbulb,
	faListCheck,
	faMinus,
	faPlus,
	faShuffle,
	faSliders,
	faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';

/* Section eyebrow label */
const SectionLabel = ({ children }: any) => (
	<div className='font-montserrat text-[1.3rem] font-semibold tracking-[0.14em] text-gray-500'>
		{children}
	</div>
);

/* Collapsible FAQ card */
const AccordionItem = ({ icon, title, isOpen, onToggle, children }: any) => (
	<div className='overflow-hidden rounded-xl border border-line bg-white'>
		<button
			onClick={onToggle}
			className='flex w-full items-center gap-[1.2rem] px-[1.6rem] py-[1.4rem] text-left transition-colors duration-150 hover:bg-canvas-soft'
		>
			<span className='flex h-[2.8rem] w-[2.8rem] shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500'>
				<FontAwesomeIcon icon={icon} className='h-[1.4rem] w-[1.4rem]' />
			</span>
			<span className='flex-1 font-colfax text-[1.6rem] text-gray-800'>
				{title}
			</span>
			<FontAwesomeIcon
				icon={isOpen ? faMinus : faPlus}
				className='h-[1.4rem] w-[1.4rem] text-gray-400'
			/>
		</button>
		{isOpen && (
			<div className='border-t border-line px-[1.6rem] py-[1.4rem] font-colfax text-[1.5rem] leading-relaxed text-gray-700'>
				{children}
			</div>
		)}
	</div>
);

/* Reference / evidence list row */
const ReferenceItem = ({ icon, title, subtitle, onClick }: any) => (
	<button
		onClick={onClick}
		className='flex w-full items-center gap-[1.4rem] px-[1.6rem] py-[1.4rem] text-left transition-colors duration-150 hover:bg-canvas-soft'
	>
		<span className='flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-lg bg-secondary-50 text-secondary-700'>
			<FontAwesomeIcon icon={icon} className='h-[1.5rem] w-[1.5rem]' />
		</span>
		<span className='flex-1'>
			<span className='block font-colfax text-[1.6rem] text-gray-800'>
				{title}
			</span>
			<span className='block font-colfax text-[1.35rem] text-gray-500'>
				{subtitle}
			</span>
		</span>
		<FontAwesomeIcon
			icon={faArrowUpRightFromSquare}
			className='h-[1.3rem] w-[1.3rem] text-gray-400'
		/>
	</button>
);

export const WelcomeScreen = () => {
	/* variable */
	const helpMeConfig = helpMeConfiguration;
	const rightForMySituationConfig = RightForMySituationConfiguration;
	const navigate = useNavigate();
	const dispatch = useDispatch();

	/* state */
	const [helpMePanel, setHelpMePanel] = useState(false);
	const [rightForMePanel, setRightForMePanel] = useState(false);
	const [shouldIUsePanel, setShouldIUsePanel] = useState(false);
	const [considerPanel, setConsiderPanel] = useState(false);

	useEffect(() => {
		dispatch(SharedActions.resetCalculator());
	}, []);

	/* methods */
	const onQuestionButtonClicked = () => {
		window.open(
			'https://share.hsforms.com/10wbTH1l5Q166QSkjpbS1Yw5ln95',
			'_blank',
		);
	};

	const navigateUserToDeepDiveCalculator = () => {
		window.open('https://dgxve27ji4aao.cloudfront.net/', '_blank');
	};

	const navigateToStepByStepGuide = () => {
		window.open(
			'https://healthbegins.org/wp-content/uploads/2023/10/Step-by-Step-Guide.docx.pdf',
			'_blank',
		);
	};

	const navigateUserToCalculator = () => {
		navigate('/calculator/workflow-selection');
	};

	const navigateToDataCheckList = () => {
		window.open(
			'https://healthbegins.org/wp-content/uploads/2023/10/Final-Data-Checklist-READY.pdf',
			'_blank',
		);
	};

	const navigateToCommonFunds = () => {
		window.open('https://www.commonwealthfund.org/', '_blank');
	};

	const navigateToScanFoundations = () => {
		window.open('https://www.thescanfoundation.org/', '_blank');
	};

	const navigateTobaselineHealthcareUtilization = () => {
		window.open(
			'https://healthbegins.org/wp-content/uploads/2023/10/Final-Average-Healthcare-Cost-and-Utilization-Tables-READY.pdf',
			'_blank',
		);
	};

	const navigateToGuideToEvidence = () => {
		window.open(
			'https://healthbegins.org/wp-content/uploads/2023/12/ROI-Calculator-Evidence-Guide-.pdf',
			'_blank',
		);
	};

	const navigateToCaseStudies = () => {
		window.open(
			'https://healthbegins.org/wp-content/uploads/2023/09/McCarthy_using_ROI_calculator_health_related_social_needs_ib_FINAL.pdf',
			'_blank',
		);
	};

	return (
		<div className='mx-auto max-w-[92rem] px-[4rem] pb-[4rem] pt-[3rem] sm:px-[1.6rem] xs:px-[1.6rem]'>
			{/* Start here intro */}
			<div className='flex items-center gap-[0.8rem] text-secondary-600'>
				<FontAwesomeIcon icon={faArrowRight} className='h-[1.3rem] w-[1.3rem]' />
				<span className='font-montserrat text-[1.3rem] font-semibold tracking-[0.14em]'>
					START HERE
				</span>
			</div>
			<p className='mt-[1rem] font-colfax text-[1.7rem] leading-relaxed text-gray-700'>
				Two ways in. Most people want the quick calculator. Pick the deep dive
				only if you're modeling several interventions at once.
			</p>

			{/* Two choice cards */}
			<div className='mt-[2.4rem] grid grid-cols-2 gap-[2rem] sm:grid-cols-1 xs:grid-cols-1'>
				{/* Quick calculator */}
				<div className='flex flex-col rounded-2xl border-[0.2rem] border-primary-400 bg-white p-[2.4rem] shadow-card'>
					<div className='flex items-center justify-between'>
						<div className='flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-xl bg-primary-50 text-primary-500'>
							<FontAwesomeIcon icon={faBolt} className='h-[2rem] w-[2rem]' />
						</div>
						<span className='rounded-full bg-secondary-50 px-[1.2rem] py-[0.5rem] font-montserrat text-[1.1rem] font-semibold tracking-[0.1em] text-secondary-700'>
							RECOMMENDED
						</span>
					</div>
					<h3 className='mt-[1.6rem] font-montserrat text-[2.2rem] text-gray-900'>
						Quick calculator
					</h3>
					<p className='mt-[0.8rem] flex-1 font-colfax text-[1.5rem] leading-relaxed text-gray-600'>
						Prepopulated with national utilization and cost benchmarks so you can
						move fast. Every field stays editable.
					</p>
					<div className='mt-[1.6rem] flex items-center gap-[1.6rem] font-colfax text-[1.4rem] text-gray-500'>
						<span className='flex items-center gap-[0.6rem]'>
							<FontAwesomeIcon icon={faClock} className='h-[1.4rem] w-[1.4rem]' />
							~20 min
						</span>
						<span className='flex items-center gap-[0.6rem]'>
							<FontAwesomeIcon
								icon={faBullseye}
								className='h-[1.4rem] w-[1.4rem]'
							/>
							One intervention
						</span>
					</div>
					<button
						onClick={navigateUserToCalculator}
						className='mt-[2rem] flex items-center justify-center gap-[0.8rem] rounded-full bg-primary-400 px-[2rem] py-[1.2rem] font-colfax text-[1.6rem] text-white shadow-soft transition-all duration-200 ease-out hover:-translate-y-[0.1rem] hover:bg-primary-500 hover:shadow-card'
					>
						Start quick calculator
						<FontAwesomeIcon icon={faArrowRight} className='h-[1.4rem] w-[1.4rem]' />
					</button>
				</div>

				{/* Deep dive calculator */}
				<div className='flex flex-col rounded-2xl border border-line bg-white p-[2.4rem]'>
					<div className='flex items-center justify-between'>
						<div className='flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-xl bg-gray-100 text-gray-600'>
							<FontAwesomeIcon icon={faSliders} className='h-[2rem] w-[2rem]' />
						</div>
						<span className='rounded-full bg-gray-100 px-[1.2rem] py-[0.5rem] font-montserrat text-[1.1rem] font-semibold tracking-[0.1em] text-gray-500'>
							ADVANCED
						</span>
					</div>
					<h3 className='mt-[1.6rem] font-montserrat text-[2.2rem] text-gray-900'>
						Deep dive calculator
					</h3>
					<p className='mt-[0.8rem] flex-1 font-colfax text-[1.5rem] leading-relaxed text-gray-600'>
						Enter your own data by hand and model multiple interventions
						together. More control, more setup.
					</p>
					<div className='mt-[1.6rem] flex items-center gap-[1.6rem] font-colfax text-[1.4rem] text-gray-500'>
						<span className='flex items-center gap-[0.6rem]'>
							<FontAwesomeIcon icon={faClock} className='h-[1.4rem] w-[1.4rem]' />
							Longer
						</span>
						<span className='flex items-center gap-[0.6rem]'>
							<FontAwesomeIcon
								icon={faLayerGroup}
								className='h-[1.4rem] w-[1.4rem]'
							/>
							Multiple interventions
						</span>
					</div>
					<button
						onClick={navigateUserToDeepDiveCalculator}
						className='mt-[2rem] flex items-center justify-center gap-[0.8rem] rounded-full border border-gray-400 bg-white px-[2rem] py-[1.2rem] font-colfax text-[1.6rem] text-gray-800 transition-all duration-200 ease-out hover:-translate-y-[0.1rem] hover:border-gray-600 hover:shadow-soft'
					>
						Open deep dive
						<FontAwesomeIcon icon={faArrowRight} className='h-[1.4rem] w-[1.4rem]' />
					</button>
				</div>
			</div>

			{/* Before you start + reference */}
			<div className='mt-[3.5rem] grid grid-cols-2 gap-[3rem] lg:gap-[3rem] md:grid-cols-1 sm:grid-cols-1 xs:grid-cols-1'>
				<div>
					<SectionLabel>BEFORE YOU START</SectionLabel>
					<div className='mt-[1.4rem] flex flex-col gap-[1rem]'>
						<AccordionItem
							icon={faCircleQuestion}
							title='What is the ROI calculator for?'
							isOpen={helpMePanel}
							onToggle={() => setHelpMePanel(!helpMePanel)}
						>
							<ListView {...helpMeConfig}></ListView>
						</AccordionItem>
						<AccordionItem
							icon={faClipboardCheck}
							title='Is my organization ready to use it?'
							isOpen={rightForMePanel}
							onToggle={() => setRightForMePanel(!rightForMePanel)}
						>
							<ListView {...rightForMySituationConfig}></ListView>
						</AccordionItem>
						<AccordionItem
							icon={faShuffle}
							title='Quick versus deep dive: which one?'
							isOpen={shouldIUsePanel}
							onToggle={() => setShouldIUsePanel(!shouldIUsePanel)}
						>
							<DeepDive></DeepDive>
						</AccordionItem>
						<AccordionItem
							icon={faLightbulb}
							title='What else should I consider?'
							isOpen={considerPanel}
							onToggle={() => setConsiderPanel(!considerPanel)}
						>
							<ConsiderRoi></ConsiderRoi>
						</AccordionItem>
					</div>
				</div>

				<div>
					<SectionLabel>REFERENCE AND EVIDENCE</SectionLabel>
					<div className='mt-[1.4rem] divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white'>
						<ReferenceItem
							icon={faBookOpen}
							title='Step-by-step guide'
							subtitle='Walk through each screen'
							onClick={navigateToStepByStepGuide}
						/>
						<ReferenceItem
							icon={faListCheck}
							title='Data checklist'
							subtitle='What to have ready'
							onClick={navigateToDataCheckList}
						/>
						<ReferenceItem
							icon={faChartLine}
							title='Baseline utilization benchmarks'
							subtitle='National reference data'
							onClick={navigateTobaselineHealthcareUtilization}
						/>
						<ReferenceItem
							icon={faBook}
							title='Guide to evidence'
							subtitle='Impact of interventions'
							onClick={navigateToGuideToEvidence}
						/>
						<ReferenceItem
							icon={faFolderOpen}
							title='Case studies'
							subtitle='Real-world ROI examples'
							onClick={navigateToCaseStudies}
						/>
					</div>
				</div>
			</div>

			{/* Footer */}
			<div className='mt-[3.5rem] border-t border-line pt-[2rem]'>
				<p className='font-colfax text-[1.4rem] leading-relaxed text-gray-500'>
					Supported by the{' '}
					<span
						className='cursor-pointer text-primary-600 hover:underline'
						onClick={navigateToCommonFunds}
					>
						Commonwealth Fund
					</span>{' '}
					and the{' '}
					<span
						className='cursor-pointer text-primary-600 hover:underline'
						onClick={navigateToScanFoundations}
					>
						SCAN Foundation
					</span>
					. Original calculator developed by Dr. Victor Tabbush.
				</p>
				<button
					onClick={onQuestionButtonClicked}
					className='mt-[1.6rem] flex items-center gap-[0.7rem] rounded-full border-[0.15rem] border-secondary px-[1.8rem] py-[0.9rem] font-colfax text-[1.5rem] text-secondary transition-all duration-200 ease-out hover:-translate-y-[0.1rem] hover:bg-secondary hover:text-white'
				>
					<FontAwesomeIcon icon={faComments} className='h-[1.5rem] w-[1.5rem]' />
					Questions and feedback
				</button>
			</div>
		</div>
	);
};
